import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';
import { CodeBlock } from '../converter-layout/code-block';

interface MetaState {
    title: string;
    description: string;
    canonical: string;
    robots: string;
    viewport: string;
    author: string;
    charset: string;
    language: string;
    ogEnabled: boolean;
    ogType: string;
    ogImage: string;
    twitterEnabled: boolean;
    twitterCard: string;
    twitterSite: string;
    copied: boolean;
}

const DEFAULTS: MetaState = {
    title: '',
    description: '',
    canonical: '',
    robots: 'index, follow',
    viewport: 'width=device-width, initial-scale=1',
    author: '',
    charset: 'UTF-8',
    language: 'en',
    ogEnabled: true,
    ogType: 'website',
    ogImage: '',
    twitterEnabled: true,
    twitterCard: 'summary_large_image',
    twitterSite: '',
    copied: false,
};

export default class MetaTagGeneratorComponent extends Component<{ feature?: string }, MetaState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: MetaState = { ...DEFAULTS };

    private esc(s: string): string {
        return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    private generate(): string {
        const s = this.state;
        const lines: string[] = [];
        lines.push(`<meta charset="${this.esc(s.charset)}">`);
        lines.push(`<meta name="viewport" content="${this.esc(s.viewport)}">`);
        if (s.language) lines.push(`<meta http-equiv="content-language" content="${this.esc(s.language)}">`);
        if (s.title) lines.push(`<title>${this.esc(s.title)}</title>`);
        if (s.description) lines.push(`<meta name="description" content="${this.esc(s.description)}">`);
        if (s.canonical) lines.push(`<link rel="canonical" href="${this.esc(s.canonical)}">`);
        if (s.robots) lines.push(`<meta name="robots" content="${this.esc(s.robots)}">`);
        if (s.author) lines.push(`<meta name="author" content="${this.esc(s.author)}">`);
        // OG/Twitter blocks require at least title + description to be useful;
        // otherwise we'd emit just the card-type tag, which fails crawler
        // unfurling and doesn't help SEO.
        const hasSocialContent = !!(s.title && s.description);
        if (s.ogEnabled && hasSocialContent) {
            lines.push(``);
            lines.push(`<!-- Open Graph -->`);
            lines.push(`<meta property="og:title" content="${this.esc(s.title)}">`);
            lines.push(`<meta property="og:description" content="${this.esc(s.description)}">`);
            lines.push(`<meta property="og:type" content="${this.esc(s.ogType)}">`);
            if (s.canonical) lines.push(`<meta property="og:url" content="${this.esc(s.canonical)}">`);
            if (s.ogImage) lines.push(`<meta property="og:image" content="${this.esc(s.ogImage)}">`);
        }
        if (s.twitterEnabled && hasSocialContent) {
            lines.push(``);
            lines.push(`<!-- Twitter Card -->`);
            lines.push(`<meta name="twitter:card" content="${this.esc(s.twitterCard)}">`);
            if (s.twitterSite) lines.push(`<meta name="twitter:site" content="${this.esc(s.twitterSite)}">`);
            lines.push(`<meta name="twitter:title" content="${this.esc(s.title)}">`);
            lines.push(`<meta name="twitter:description" content="${this.esc(s.description)}">`);
            if (s.ogImage) lines.push(`<meta name="twitter:image" content="${this.esc(s.ogImage)}">`);
        }
        return lines.join('\n');
    }

    copyOutput = () => {
        navigator.clipboard.writeText(this.generate()).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private inputField(field: keyof MetaState, label: string, placeholder = '', type: 'text' | 'url' | 'textarea' = 'text') {
        const val = this.state[field] as string;
        return (
            <div className={genStyles.field}>
                <label htmlFor={`meta-${field}`}>{label}</label>
                {type === 'textarea' ? (
                    <textarea id={`meta-${field}`} value={val} placeholder={placeholder} rows={2}
                        onChange={e => this.setState({ [field]: e.target.value } as any)} />
                ) : (
                    <input id={`meta-${field}`} type={type} value={val} placeholder={placeholder}
                        onChange={e => this.setState({ [field]: e.target.value } as any)} />
                )}
            </div>
        );
    }

    render() {
        const out = this.generate();
        const { copied, ogEnabled, twitterEnabled, ogType, twitterCard, robots } = this.state;
        const lineCount = out.split('\n').filter(Boolean).length;
        const checkRow = (label: string, checked: boolean, onChange: (b: boolean) => void) => (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
                {label}
            </label>
        );

        const form = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Core meta tags</span></div>
                    {this.inputField('title', 'Title (50–60 chars)', 'My Page Title')}
                    {this.inputField('description', 'Description (120–160 chars)', 'Page description for search engines', 'textarea')}
                    {this.inputField('canonical', 'Canonical URL', 'https://example.com/page', 'url')}
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="meta-robots">Robots</label>
                            <select id="meta-robots" value={robots} onChange={e => this.setState({ robots: e.target.value })}>
                                <option value="index, follow">index, follow</option>
                                <option value="index, nofollow">index, nofollow</option>
                                <option value="noindex, follow">noindex, follow</option>
                                <option value="noindex, nofollow">noindex, nofollow</option>
                            </select>
                        </div>
                        {this.inputField('language', 'Language', 'en')}
                    </div>
                    {this.inputField('author', 'Author (optional)')}
                </div>

                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Open Graph (Facebook, LinkedIn)</span></div>
                    {checkRow('Include Open Graph tags', ogEnabled, v => this.setState({ ogEnabled: v }))}
                    {ogEnabled && <>
                        <div className={genStyles.field}>
                            <label htmlFor="og-type">OG Type</label>
                            <select id="og-type" value={ogType} onChange={e => this.setState({ ogType: e.target.value })}>
                                <option value="website">website</option>
                                <option value="article">article</option>
                                <option value="product">product</option>
                                <option value="profile">profile</option>
                                <option value="video.other">video.other</option>
                            </select>
                        </div>
                        {this.inputField('ogImage', 'Social Image URL', 'https://example.com/og.png', 'url')}
                    </>}
                </div>

                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Twitter Card</span></div>
                    {checkRow('Include Twitter Card tags', twitterEnabled, v => this.setState({ twitterEnabled: v }))}
                    {twitterEnabled && <>
                        <div className={genStyles.field}>
                            <label htmlFor="tw-card">Card Type</label>
                            <select id="tw-card" value={twitterCard} onChange={e => this.setState({ twitterCard: e.target.value })}>
                                <option value="summary">summary</option>
                                <option value="summary_large_image">summary_large_image</option>
                                <option value="app">app</option>
                                <option value="player">player</option>
                            </select>
                        </div>
                        {this.inputField('twitterSite', '@site (optional)', '@example')}
                    </>}
                </div>
            </>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Generated meta tags"
                previewMeta={`${lineCount} line${lineCount === 1 ? '' : 's'}`}
                preview={<CodeBlock code={out} language="html" wrapLines maxHeight={500} minHeight={320} ariaLabel="Generated meta tags" />}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput}>{copied ? '✓ Copied' : '📋 Copy'}</button>
                }
            />
        );
    }
}
